import React from "react";
import { useVisibility, type VisibilityLevel } from "@/contexts/VisibilityContext";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { CompactVisibilityRow } from "./CompactVisibilityRow";
import { BulkActions } from "./BulkActions";
import type { VisibilityRegistryPage } from "@/config/visibilityRegistry";

interface PageAccordionProps {
  page: VisibilityRegistryPage;
  filteredSections?: { id: string; label: string }[];
}

export function PageAccordion({ page, filteredSections }: PageAccordionProps) {
  const { getVisibility } = useVisibility();

  const sections = filteredSections ?? page.sections;

  // Calculate stats for badges
  const hiddenCount = sections.filter(
    (s) => getVisibility(`${page.id}.${s.id}`) === "hidden"
  ).length;

  const restrictedCount = sections.filter((s) => {
    const vis = getVisibility(`${page.id}.${s.id}`);
    return vis === "member" || vis === "admin";
  }).length;

  const pageVisibility = getVisibility(page.id);

  return (
    <AccordionItem value={page.id} className="border rounded-lg px-1">
      <AccordionTrigger className="hover:no-underline py-3 px-3">
        <div className="flex items-center gap-2 flex-wrap text-left">
          <span className="font-semibold">{page.label}</span>
          <Badge variant="outline" className="text-xs font-normal">
            {sections.length} section{sections.length !== 1 ? "s" : ""}
          </Badge>
          {hiddenCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {hiddenCount} hidden
            </Badge>
          )}
          {restrictedCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {restrictedCount} restricted
            </Badge>
          )}
          {pageVisibility !== "public" && (
            <Badge
              variant={pageVisibility === "hidden" ? "destructive" : "default"}
              className="text-xs"
            >
              Page: {pageVisibility}
            </Badge>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-3">
        {/* Page-level visibility control */}
        <div className="mb-3 border-b pb-3">
          <div className="text-xs font-semibold text-muted-foreground mb-2 px-3">
            Page Access Level
          </div>
          <CompactVisibilityRow
            moduleType="page"
            moduleId={page.id}
            title={`${page.label} Page`}
          />
        </div>

        {/* Bulk actions for sections */}
        {sections.length > 0 && (
          <>
            <BulkActions pageId={page.id} sections={sections} />

            {/* Section rows */}
            <div className="text-xs font-semibold text-muted-foreground mt-3 mb-2 px-3">
              Sections
            </div>
            <div className="border rounded-md overflow-hidden">
              {sections.map((section) => (
                <CompactVisibilityRow
                  key={section.id}
                  moduleType="section"
                  moduleId={`${page.id}.${section.id}`}
                  title={section.label}
                />
              ))}
            </div>
          </>
        )}

        {sections.length === 0 && (
          <p className="text-sm text-muted-foreground px-3">
            No sections match your filter.
          </p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
